import { createFileRoute, Outlet, useLocation, useNavigate } from "@tanstack/react-router";
import { useAuth } from "@/hooks/useAuth";
import { useEffect } from "react";
import { Loader2 } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";
import { DashboardShell } from "@/components/dashboard/DashboardShell";
import { useIdleLogout } from "@/hooks/useIdleLogout";

export const Route = createFileRoute("/dashboard")({
  head: () => ({ meta: [{ name: "robots", content: "noindex,nofollow" }] }),
  component: DashboardRouter,
});

function DashboardRouter() {
  useIdleLogout(15 * 60 * 1000);
  const { user, loading, primaryRole, mfa } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const isDashboardIndex = location.pathname === "/dashboard";

  // Compulsory 2FA for admins and doctors: any such role without an enrolled
  // authenticator is bounced to enrollment before they can use ANY dashboard
  // page. Patients are unaffected. Runs on every /dashboard/* path, not just the
  // index, so deep links can't slip past the gate.
  const needsMfaEnrollment =
    !!user && mfa.ready && !mfa.enrolled && (primaryRole === "admin" || primaryRole === "doctor");
  useEffect(() => {
    if (loading) return;
    if (needsMfaEnrollment) navigate({ to: "/auth/mfa-setup" as any, replace: true });
  }, [loading, needsMfaEnrollment, navigate]);

  useEffect(() => {
    if (!isDashboardIndex) return;
    if (loading) return;
    if (needsMfaEnrollment) return; // MFA gate handles the redirect
    if (!user) { navigate({ to: "/login" }); return; }

    if (typeof window !== "undefined") {
      const stashed = localStorage.getItem("heallexa_post_login_redirect");
      if (stashed) {
        localStorage.removeItem("heallexa_post_login_redirect");
        window.location.replace(stashed);
        return;
      }
    }

    const metaRole = (user.user_metadata as any)?.account_type as string | undefined;
    const role = primaryRole ?? (metaRole === "doctor" ? "doctor" : metaRole === "patient" ? "patient" : null);

    if (!role) {
      const tid = setTimeout(() => navigate({ to: "/dashboard/patient", search: { tab: undefined } }), 2500);
      return () => clearTimeout(tid);
    }

    if (role === "admin") navigate({ to: "/dashboard/admin" });
    else if (role === "doctor") navigate({ to: "/dashboard/doctor", search: { billing: undefined, pid: undefined, tab: undefined, verified: false, verificationSessionId: undefined, status: undefined } });
    else navigate({ to: "/dashboard/patient", search: { tab: undefined } });
  }, [user, loading, primaryRole, navigate, isDashboardIndex, needsMfaEnrollment]);

  // Hold the UI while we bounce an un-enrolled admin/doctor to MFA setup, so the
  // protected page never flashes.
  if (needsMfaEnrollment) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground gap-2">
        <Loader2 className="h-4 w-4 animate-spin" /> Redirecting to two-factor setup…
      </div>
    );
  }

  if (!isDashboardIndex) {
    return (
      <DashboardShell>
        <Outlet />
      </DashboardShell>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center text-muted-foreground gap-2">
      <Loader2 className="h-4 w-4 animate-spin" /> {t.common.loadingDash}
    </div>
  );
}
